<template>
    <div class="position-relative">
        <div class="dropdown dropdown-fluid timeline-suggestion-picker" :class="{ 'is-visible' : isVisible }">
            <div class="dropdown-content dropdown-content-help">
                Press <strong>enter</strong> to select
                <v-icon icon="arrow-left"/>
                <v-icon icon="arrow-right"/>
                <v-icon icon="arrow-up"/>
                <v-icon icon="arrow-down"/>
                or <strong>tab</strong> to navigate, <strong>esc</strong> to dismiss
            </div>
            <div class="dropdown-content">
                <div v-for="(suggestionItem, index) in suggestions"
                     @click="suggestionSelected(suggestionItem)"
                     @mouseover="suggestionIndex = index"
                     class="dropdown-item"
                     :class="{'is-active': index === suggestionIndex}">
                    <v-icon :icon="suggestionItem.icon" class="inline-s"/>
                    <span>{{suggestionItem.picker}}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import {vIcon} from '@/components/form';

    const keyCodes = {
        tab: 9,
        upArrow: 38,
        downArrow: 40,
        enter: 13,
        esc: 27,
    };

    const spaceRegex = /(\s+)/g;

    const debounceTime = 200;

    export default {
        components: {vIcon},
        name: 'suggestion-picker',
        props: {
            providers: Array,
        },
        data() {
            return {
                previousContent: '',
                suggestions: [],
                suggestionIndex: 0,
                isVisible: false,
            };
        },
        computed: {
            maxSuggestionIndex() {
                return this.suggestions.length - 1;
            },
        },
        methods: {
            moveSelectionDown() {
                if (this.suggestionIndex < this.maxSuggestionIndex) {
                    this.suggestionIndex += 1;
                } else {
                    this.suggestionIndex = 0;
                }
            },
            moveSelectionUp() {
                if (this.suggestionIndex > 0) {
                    this.suggestionIndex -= 1;
                } else {
                    this.suggestionIndex = this.maxSuggestionIndex;
                }
            },
            isActionKey({keyCode}) {
                return this.isVisible && Object.values(keyCodes).includes(keyCode);
            },
            keyUp({keyCode}) {
                if ([keyCodes.downArrow, keyCodes.tab].includes(keyCode)) {
                    this.moveSelectionDown();
                } else if (keyCode === keyCodes.upArrow) {
                    this.moveSelectionUp();
                } else if (keyCode === keyCodes.enter) {
                    this.suggestionSelected(this.suggestions[this.suggestionIndex]);
                } else if (keyCode === keyCodes.esc) {
                    this.hideSuggestions();
                }
            },
            hasToken(word) {
                return !!this.getProvider(word);
            },
            getProvider(word) {
                const wordToken = word.slice(0, 1);
                return this.providers.find(({token}) => token === wordToken);
            },
            suggestionSelected({value, isPartial, wordIndex}) {
                const previousWordsAndSpaces = this.previousContent.split(spaceRegex);
                previousWordsAndSpaces[wordIndex] = `${value}${isPartial ? '' : ' '}`;
                const updatedInput = previousWordsAndSpaces.join('');
                if (isPartial) {
                    this.checkForSuggestions(updatedInput);
                } else {
                    this.previousContent = updatedInput;
                }
                this.hideSuggestions();
                this.$emit('input', updatedInput);
            },
            hideSuggestions() {
                this.isVisible = false;
            },
            async showSuggestions(word, wordIndex) {
                const provider = this.getProvider(word);
                if (provider) {
                    this.suggestionIndex = 0;
                    this.suggestions = await this.getSuggestions(word, wordIndex, provider);
                    this.isVisible = this.suggestions.length !== 0;
                }
            },
            async getSuggestions(word, wordIndex, {
                token, resources, divider, staticValues,
            }) {
                const suggestions = await (() => {
                    const resourceWord = word.slice(1);
                    const resourceNames = Object.keys(resources);
                    if (resourceNames.length === 1) {
                        // Since there is only one.. The resource is known so discover values.
                        const defaultResource = resources[resourceNames[0]];
                        return this.fetchProviderResource(defaultResource, resourceWord, staticValues);
                    }
                    if (resourceWord) {
                        const [resourceName, resourceKeyPrefix] = resourceWord.split(divider);
                        const resource = resources[resourceName];
                        if (resource) {
                            // The resource is known so discover values
                            return this.fetchProviderResource(resource, resourceKeyPrefix, staticValues);
                        }
                    }
                    // Otherwise select a resource from the options
                    return this.getResourceSuggestions(
                        token, resources, resourceNames, divider, resourceWord,
                    );
                })();
                return suggestions.map(suggestion => ({...suggestion, word, wordIndex}));
            },
            getResourceSuggestions(token, resources, resourceNames, divider, resourcePrefix) {
                return resourceNames
                    .filter(resourceName => resourceName.startsWith(resourcePrefix))
                    .map((resourceName) => {
                        const {icon} = resources[resourceName];
                        return {
                            value: `${token}${resourceName}${divider}`,
                            icon,
                            picker: resourceName,
                            isPartial: true,
                        };
                    });
            },
            checkForSuggestions(contentText) {
                if (this.lastCheckHandler !== null) {
                    clearTimeout(this.lastCheckHandler);
                }
                if (this.previousContent === contentText) {
                    return;
                }
                const debounceFunction = async () => {
                    const wordsAndSpaces = contentText.split(spaceRegex);
                    const previousWordsAndSpaces = this.previousContent.split(spaceRegex);
                    const partialWordIndex = wordsAndSpaces.findIndex((wordOrSpace, index) => previousWordsAndSpaces[index] !== wordOrSpace && this.hasToken(wordOrSpace));
                    this.previousContent = contentText;
                    if (partialWordIndex >= 0) {
                        await this.showSuggestions(wordsAndSpaces[partialWordIndex], partialWordIndex);
                    } else {
                        this.hideSuggestions();
                    }
                    this.lastCheckHandler = null;
                };
                this.lastCheckHandler = setTimeout(debounceFunction, debounceTime);
            },
            async fetchProviderResource(resource, resourceKeyPrefix, staticValues) {
                const params = {q: resourceKeyPrefix, limit: 20, offset: 0};
                const {items} = await this.$api(({[resource.name]: apiRes}) => apiRes.getAll(params));
                return [
                    ...staticValues || [],
                    ...items.map(({fields}) => (
                        {
                            value: resource.entityValueMask(fields),
                            icon: resource.icon,
                            picker: resource.entityPickerMask(fields),
                        })),
                ];
            },
        },
    };
</script>
